/**
 * Camel movement: how a die roll translates into board updates.
 *
 * Stacking rules:
 *   - A camel moves with every camel ABOVE it in its current stack (i.e. the
 *     ones riding on its back). The group keeps its relative order.
 *   - **Racing camels move forward.** The moving group lands ON TOP of the
 *     destination stack.
 *   - **Crazy camels move backward.** If the destination already contains
 *     the OTHER crazy camel, the moving group goes UNDER the other crazy
 *     camel's group (the other crazy camel and anything above it stays as
 *     a stack-top island; the moving group is inserted beneath that island).
 *     Otherwise the moving group lands on top, same as racing camels.
 *   - **Finish line.** A racing camel that moves past space `BOARD_SIZE`
 *     finishes the race. If it was carrying any other racing camels, they
 *     finish too, preserving stack order (so the camel on top of the moving
 *     group is the first to cross). Crazy camels are clamped to space 1 if
 *     they would move past the start; they cannot leave the board.
 *
 * All functions in this file are pure: they take state, return new state.
 */

import type { CamelColor, CamelUpState, CrazyColor, RacingColor } from '../types'
import { BOARD_SIZE, CRAZY_COLORS, RACING_COLORS } from './constants'

const RACING_SET: ReadonlySet<CamelColor> = new Set<CamelColor>(RACING_COLORS)
const CRAZY_SET: ReadonlySet<CamelColor> = new Set<CamelColor>(CRAZY_COLORS)

export function isRacingColor(c: CamelColor): c is RacingColor {
  return RACING_SET.has(c)
}

export function isCrazyColor(c: CamelColor): c is CrazyColor {
  return CRAZY_SET.has(c)
}

/** Find the (space, heightIndex) of a camel on the board. Throws if missing. */
export function findCamel(
  state: CamelUpState,
  camel: CamelColor,
): { space: number; height: number } {
  for (let s = 1; s <= BOARD_SIZE; s++) {
    const stack = state.spaces[s]
    const h = stack.indexOf(camel)
    if (h >= 0) return { space: s, height: h }
  }
  throw new Error(`Camel ${camel} is not on the board`)
}

/**
 * Move a racing camel forward by `value` spaces. Returns the next state and
 * a flag indicating whether at least one racing camel crossed the finish.
 */
export function moveRacingForward(
  state: CamelUpState,
  camel: RacingColor,
  value: number,
): { state: CamelUpState; finished: boolean } {
  // Crazy camels can't be on the same "racing" die track, but a racing
  // camel can be carrying / being carried by anything. If the racing camel
  // we're moving is already off the board (in `finished`), this is a no-op.
  if (state.finished.includes(camel)) {
    return { state, finished: false }
  }

  const { space, height } = findCamel(state, camel)
  const fromStack = state.spaces[space]
  const moving = fromStack.slice(height)
  const remaining = fromStack.slice(0, height)

  const dest = space + value
  const nextSpaces = { ...state.spaces, [space]: remaining }
  let nextFinished = state.finished
  let didFinish = false

  if (dest > BOARD_SIZE) {
    // Whole moving group finishes. The camel on TOP of the moving group is
    // ahead, so it crosses first and ranks highest. `moving` is stored
    // bottom-to-top, so we walk it top-down when recording finish order.
    // This keeps `finished` consistent with `rankRacingCamels`, which ranks
    // on-board camels top-of-stack-first.
    const finishers: RacingColor[] = []
    for (let i = moving.length - 1; i >= 0; i--) {
      const c = moving[i]
      if (isRacingColor(c)) finishers.push(c)
      // Crazy camels that get carried past space 16 don't make sense in
      // normal play — defensive: just drop them off at BOARD_SIZE. In
      // practice a crazy camel can't be carried forward (only racing
      // camels carry, and crazy camels only stack when going backward).
    }
    nextFinished = [...state.finished, ...finishers]
    didFinish = finishers.length > 0
  } else {
    // Land on top of destination stack.
    nextSpaces[dest] = [...state.spaces[dest], ...moving]
  }

  return {
    state: { ...state, spaces: nextSpaces, finished: nextFinished },
    finished: didFinish,
  }
}

/**
 * Move a crazy camel backward by `value` spaces. Returns the next state.
 * (Crazy camels never trigger race-end.)
 */
export function moveCrazyBackward(
  state: CamelUpState,
  camel: CrazyColor,
  value: number,
): { state: CamelUpState } {
  const { space, height } = findCamel(state, camel)
  const fromStack = state.spaces[space]
  const moving = fromStack.slice(height)
  const remaining = fromStack.slice(0, height)

  // Clamp to space 1 if the crazy camel would go past the start.
  const rawDest = space - value
  const dest = Math.max(1, rawDest)

  const nextSpaces = { ...state.spaces, [space]: remaining }
  const otherCrazy: CrazyColor = camel === 'black' ? 'white' : 'black'
  const destStack = state.spaces[dest]
  const otherIdx = destStack.indexOf(otherCrazy)

  if (otherIdx >= 0) {
    // Insert moving group UNDER the other crazy camel's island.
    // destStack = [below..., otherCrazy, above...] — we want
    // [below..., ...moving, otherCrazy, above...].
    const below = destStack.slice(0, otherIdx)
    const otherIsland = destStack.slice(otherIdx)
    nextSpaces[dest] = [...below, ...moving, ...otherIsland]
  } else {
    // Land on top.
    nextSpaces[dest] = [...destStack, ...moving]
  }

  return { state: { ...state, spaces: nextSpaces } }
}

/**
 * Current ranking of racing camels, 1st -> last. Finished camels rank by
 * finish order; camels still on the board rank by (space desc, height desc).
 *
 * Throws if not all 5 racing camels are accounted for (defensive).
 */
export function rankRacingCamels(state: CamelUpState): RacingColor[] {
  const ranked: RacingColor[] = [...state.finished]

  const remaining: Array<{ color: RacingColor; space: number; height: number }> = []
  for (let s = 1; s <= BOARD_SIZE; s++) {
    const stack = state.spaces[s]
    for (let h = 0; h < stack.length; h++) {
      const c = stack[h]
      if (isRacingColor(c)) remaining.push({ color: c, space: s, height: h })
    }
  }
  // Sort highest space first, ties broken by stack height (top of stack
  // is ahead — Camel Up tie-break).
  remaining.sort((a, b) => {
    if (a.space !== b.space) return b.space - a.space
    return b.height - a.height
  })
  ranked.push(...remaining.map((r) => r.color))

  if (ranked.length !== RACING_COLORS.length) {
    throw new Error(
      `rankRacingCamels: expected ${RACING_COLORS.length} camels, got ${ranked.length}`,
    )
  }
  return ranked
}
